import React, { useEffect, useMemo, useState } from 'react';
import { UserPlus, Trash2, Ban, RotateCw, Loader2, X, ShieldCheck, ShieldX, Clock, Search } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useToast } from '../../hooks/use-toast';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  try {
    const a = JSON.parse(localStorage.getItem('dhs-admin') || '{}');
    return a?.token ? { Authorization: `Bearer ${a.token}` } : {};
  } catch { return {}; }
};

const AdminAgents = () => {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [filter, setFilter] = useState('all'); // all | pending | active | rejected
  const [query, setQuery] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/admin/agents`, { headers: authHeaders() });
      if (res.status === 401 || res.status === 403) {
        setRows([]);
      } else {
        const j = await res.json();
        setRows(Array.isArray(j) ? j : []);
      }
    } catch {
      setRows([]);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((a) => {
      if (filter === 'pending' && a.status !== 'pending_approval') return false;
      if (filter === 'active' && a.status !== 'active') return false;
      if (filter === 'rejected' && a.status !== 'rejected') return false;
      if (q && !(a.email || '').toLowerCase().includes(q) && !(a.name || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rows, filter, query]);

  const pendingCount = rows.filter((a) => a.status === 'pending_approval').length;

  const createAgent = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return toast({ title: 'Enter the agent’s name and email' });
    setBusy('create');
    try {
      const res = await fetch(`${BACKEND}/api/admin/agents`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ name: form.name.trim(), email: form.email.trim().toLowerCase(), phone: form.phone.trim() }),
      });
      const j = await res.json();
      if (!res.ok) return toast({ title: j.detail || 'Could not create agent' });
      toast({ title: 'Agent created', description: `${form.email} is active — they can sign in with a one-time code straight away.` });
      setForm({ name: '', email: '', phone: '' });
      setOpen(false);
      load();
    } finally { setBusy(''); }
  };

  const approve = async (email) => {
    setBusy('approve-' + email);
    try {
      const res = await fetch(`${BACKEND}/api/admin/agents/${encodeURIComponent(email)}/approve`, { method: 'POST', headers: authHeaders() });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) return toast({ title: j.detail || 'Approval failed' });
      toast({ title: 'Agent approved', description: `${email} has been notified by email and can sign in now.` });
      load();
    } finally { setBusy(''); }
  };

  const reject = async (email) => {
    if (!window.confirm(`Reject agent ${email}? They will be notified by email.`)) return;
    setBusy('reject-' + email);
    try {
      const res = await fetch(`${BACKEND}/api/admin/agents/${encodeURIComponent(email)}/reject`, { method: 'POST', headers: authHeaders() });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) return toast({ title: j.detail || 'Rejection failed' });
      toast({ title: 'Agent rejected', description: `${email} has been notified.` });
      load();
    } finally { setBusy(''); }
  };

  const toggleStatus = async (agent) => {
    const next = agent.status === 'disabled' ? 'active' : 'disabled';
    setBusy('toggle-' + agent.email);
    try {
      const res = await fetch(`${BACKEND}/api/admin/agents/${encodeURIComponent(agent.email)}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ status: next }),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); return toast({ title: j.detail || 'Update failed' }); }
      toast({ title: `Agent ${next === 'disabled' ? 'disabled' : 'enabled'}` });
      load();
    } finally { setBusy(''); }
  };

  const remove = async (email) => {
    if (!window.confirm(`Delete agent ${email}? This cannot be undone.`)) return;
    setBusy('del-' + email);
    try {
      const res = await fetch(`${BACKEND}/api/admin/agents/${encodeURIComponent(email)}`, { method: 'DELETE', headers: authHeaders() });
      if (!res.ok) { const j = await res.json().catch(() => ({})); return toast({ title: j.detail || 'Delete failed' }); }
      toast({ title: 'Agent deleted' });
      load();
    } finally { setBusy(''); }
  };

  const badge = (status) => {
    const map = {
      active: 'bg-[#00b67a]/15 text-[#00b67a]',
      pending_approval: 'bg-[#FFEDD6] text-[#FF5C39]',
      rejected: 'bg-red-100 text-red-700',
      disabled: 'bg-[#1F0068]/10 text-[#1F0068]/70',
    };
    const label = { pending_approval: 'pending approval' }[status] || (status || 'unknown').replace('_', ' ');
    return <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${map[status] || map.pending_approval}`}>{label}</span>;
  };

  const FilterPill = ({ id, label, count }) => (
    <button
      data-testid={`agent-filter-${id}`}
      onClick={() => setFilter(id)}
      className={`h-9 px-4 rounded-full text-xs font-semibold transition-all inline-flex items-center gap-1.5 ${filter === id ? 'bg-[#1F0068] text-white shadow' : 'bg-white text-[#1F0068]/70 border border-[#1F0068]/10 hover:border-[#1F0068]/30'}`}
    >
      {label}
      {typeof count === 'number' && count > 0 && <span className={`px-1.5 rounded-full text-[10px] ${filter === id ? 'bg-white/20' : 'bg-[#FF5C39] text-white'}`}>{count}</span>}
    </button>
  );

  return (
    <>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-[#1F0068] tracking-tight">Agents</h1>
          {pendingCount > 0 && (
            <p className="text-xs text-[#FF5C39] font-semibold mt-1 inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {pendingCount} agent application{pendingCount === 1 ? '' : 's'} awaiting your review</p>
          )}
        </div>
        <Button data-testid="create-agent-btn" onClick={() => setOpen(true)} className="rounded-full bg-[#1F0068] hover:bg-[#2A0C7A] text-white gap-1.5">
          <UserPlus className="w-4 h-4" /> Create agent
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4 items-center justify-between">
        <div className="flex flex-wrap gap-2">
          <FilterPill id="all" label="All" count={rows.length} />
          <FilterPill id="pending" label="Pending" count={pendingCount} />
          <FilterPill id="active" label="Active" count={rows.filter((a) => a.status === 'active').length} />
          <FilterPill id="rejected" label="Rejected" count={rows.filter((a) => a.status === 'rejected').length} />
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#1F0068]/50" />
          <Input data-testid="agent-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name or email…" className="h-9 pl-9 pr-3 w-56 rounded-full bg-white border-[#1F0068]/10 text-sm" />
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#1F0068]/5 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[#1F0068]/60 text-sm"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading agents…</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FFF6EB] text-[#1F0068]/70 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-5 py-3">Name</th>
                  <th className="text-left px-5 py-3">Email</th>
                  <th className="text-left px-5 py-3">Phone</th>
                  <th className="text-left px-5 py-3">Status</th>
                  <th className="text-left px-5 py-3">Applied</th>
                  <th className="text-right px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1F0068]/5">
                {filtered.map((a) => (
                  <tr key={a.email} data-testid={`agent-row-${a.email}`} className="hover:bg-[#FFF6EB]">
                    <td className="px-5 py-3 font-semibold text-[#1F0068]">
                      {a.name || '—'}
                      {a.self_registered && <div className="text-[10px] text-[#1F0068]/50 font-normal">Self-registered</div>}
                    </td>
                    <td className="px-5 py-3 text-[#1F0068]/80">{a.email}</td>
                    <td className="px-5 py-3 text-[#1F0068]/70 text-xs">{a.phone || '—'}</td>
                    <td className="px-5 py-3">{badge(a.status)}</td>
                    <td className="px-5 py-3 text-[#1F0068]/70 text-xs">{a.created_at ? new Date(a.created_at).toLocaleDateString('en-GB') : '—'}</td>
                    <td className="px-5 py-3 text-right space-x-1 whitespace-nowrap">
                      {a.status === 'pending_approval' && (
                        <>
                          <Button data-testid={`approve-agent-${a.email}`} size="sm" onClick={() => approve(a.email)} disabled={busy === 'approve-' + a.email} className="rounded-full text-xs bg-[#00b67a] hover:bg-[#00a06b] text-white gap-1">
                            {busy === 'approve-' + a.email ? <Loader2 className="w-3 h-3 animate-spin" /> : <><ShieldCheck className="w-3 h-3" /> Approve</>}
                          </Button>
                          <Button data-testid={`reject-agent-${a.email}`} size="sm" variant="outline" onClick={() => reject(a.email)} disabled={busy === 'reject-' + a.email} className="rounded-full text-xs border-red-400 text-red-600 gap-1">
                            <ShieldX className="w-3 h-3" /> Reject
                          </Button>
                        </>
                      )}
                      {(a.status === 'active' || a.status === 'disabled') && (
                        <Button data-testid={`toggle-agent-${a.email}`} size="sm" variant="outline" onClick={() => toggleStatus(a)} disabled={busy === 'toggle-' + a.email} className="rounded-full text-xs gap-1">
                          {a.status === 'disabled' ? <><RotateCw className="w-3 h-3" /> Enable</> : <><Ban className="w-3 h-3" /> Disable</>}
                        </Button>
                      )}
                      <Button data-testid={`delete-agent-${a.email}`} size="sm" variant="outline" onClick={() => remove(a.email)} disabled={busy === 'del-' + a.email} className="rounded-full text-xs border-[#FF5C39] text-[#FF5C39]"><Trash2 className="w-3 h-3" /></Button>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && <tr><td colSpan="6" className="px-5 py-10 text-center text-[#1F0068]/60">No agents match this filter.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {open && (
        <div data-testid="create-agent-dialog" className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <form onSubmit={createAgent} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#FF5C39] font-bold">Invite an agent</div>
                <h2 className="text-xl font-bold text-[#1F0068] mt-1">Create agent account</h2>
                <p className="text-xs text-[#1F0068]/70 mt-1">The agent will be active immediately and can sign in with a one-time code sent to their email.</p>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="text-[#1F0068]/60 hover:text-[#1F0068]"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-[#1F0068] font-semibold">Full name</Label>
                <Input data-testid="new-agent-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe" className="h-11 mt-2 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" autoFocus />
              </div>
              <div>
                <Label className="text-[#1F0068] font-semibold">Work email</Label>
                <Input data-testid="new-agent-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jane@company.com" className="h-11 mt-2 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" />
              </div>
              <div>
                <Label className="text-[#1F0068] font-semibold">Phone <span className="text-[#1F0068]/50 font-normal">(optional)</span></Label>
                <Input data-testid="new-agent-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="07700 900 123" className="h-11 mt-2 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
              <Button data-testid="new-agent-submit" type="submit" disabled={busy === 'create'} className="rounded-full bg-[#FF5C39] hover:bg-[#e0492b] text-white gap-1.5">
                {busy === 'create' ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UserPlus className="w-4 h-4" /> Create agent</>}
              </Button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default AdminAgents;
